import { Box, Typography, Container, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';

import MacbookProMockup from '@/components/mockups/MacbookProMockup';

// Custom styled components
const FeatureTab = styled(Box)<{ active?: boolean }>(({ theme, active }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  padding: '16px 0 16px 24px',
  borderLeft: active ? '4px solid #FFBEB4' : '4px solid #F5F5F5',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderLeft: '4px solid #FFBEB4',
  },
}));

// const LearnMoreButton = styled(Button)(({ theme }) => ({
//   display: 'flex',
//   alignItems: 'center',
//   gap: '6px',
//   textTransform: 'none',
//   padding: 0,
//   color: theme.palette.primary.main,
//   '&:hover': {
//     backgroundColor: 'transparent',
//   },
// }));

// Define feature data type
interface FeatureItem {
  id: number;
  title: string;
  description: string;
  image: string;
}

const PropertyManagerFeaturesSection: React.FC = () => {
  // const theme = useTheme();
  const [activeFeature, setActiveFeature] = useState(0);

  // Feature data
  const features: FeatureItem[] = [
    {
      id: 0,
      title: '24/7 Communication',
      description:
        "Tenants expect instant answers — now you don't have to be the one delivering them. Our AI responds to messages, filters requests, and keeps everyone in the loop, day or night.",
      image: '/assets/images/landing/screen1_.jpg',
    },
    {
      id: 1,
      title: 'Streamlined Workflows',
      description:
        'No more juggling spreadsheets, sticky notes, or endless email threads. Terr.ai organizes everything — from leases to maintenance — in one place, and automates what slows you down.',
      image: '/assets/images/landing/screen2_.jpg',
    },
    {
      id: 2,
      title: 'Enhanced Efficiency',
      description:
        "Reclaim your time. Whether you're growing your rental portfolio or managing for others, Terr.ai helps you operate like a team — even if you're a team of one.",
      image: '/assets/images/landing/screen3_.jpg',
    },
  ];

  const handleFeatureClick = (id: number) => {
    setActiveFeature(id);
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        backgroundImage: 'url("/assets/images/landing/seamless.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '64px',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1,
        },
      }}
    >
      {/* Title section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ textAlign: 'left', mb: 4 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: '#FFBEB4',
              fontWeight: 600,
              mb: 1.5,
            }}
          >
            Manage more. Stress less. Terr.ai has your back.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Nohemi, sans-serif',
              fontSize: { xs: '28px', md: '36px' },
              fontWeight: 600,
              letterSpacing: '0.02em',
              mb: 2.5,
              color: '#FFFFFF',
            }}
          >
            A Seamless Experience for Property Managers & Landlords
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '16px', md: '20px' },
              maxWidth: '800px',
              color: '#FFFFFF',
            }}
          >
            Whether you're managing a few units or overseeing entire buildings, Terr.ai simplifies
            your day-to-day. Our AI-powered tools handle communication, coordination, and follow-up
            — so you can focus on people, not paperwork.
          </Typography>
        </Box>
      </Container>

      {/* Features and mockup section */}
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={8}>
          {/* Feature tabs */}
          <Box sx={{ width: { xs: '100%', md: '41.67%' } }}>
            <Box display="flex" flexDirection="column">
              {features.map(({ id, title, description }) => (
                <FeatureTab
                  key={id}
                  active={activeFeature === id}
                  onClick={() => handleFeatureClick(id)}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: 'Nohemi, sans-serif',
                        fontSize: '20px',
                        fontWeight: 600,
                        mb: 0.5,
                        color: '#FFFFFF',
                      }}
                    >
                      {title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        color: 'rgba(255, 255, 255, 0.8)',
                      }}
                    >
                      {description}
                    </Typography>
                  </Box>
                  {/* <LearnMoreButton disableRipple>
                    Learn more
                    <ArrowRightIcon
                      sx={{
                        width: 20,
                        height: 20,
                        color: theme.palette.primary.main,
                      }}
                    />
                  </LearnMoreButton> */}
                </FeatureTab>
              ))}
            </Box>
          </Box>

          {/* Mockup image */}
          <Box sx={{ width: { xs: '100%', md: '58.33%' } }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <MacbookProMockup screenshot={features[activeFeature].image} />
            </Box>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default PropertyManagerFeaturesSection;
